import { useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import { WorkerAvatar } from "../../../../../UI";
import { openTicketDetails } from "../../../../../../redux/actions/services.actions";
import { disableScroll } from "../../../../../../utils";

import { Chevron, WarnIcon } from "../icons";
import { themeForErp } from "../helpers/status.map";
import { packLanes } from "../helpers/lane-packing";
import {
  formatHour,
  formatHourShort,
  clampToWindow
} from "../helpers/time";

import { TimelineBodyStyles } from "./timelineBody.styles";

const HOUR_WIDTH = 120;
const UNASSIGNED_ROW_H = 44;

// Concatenate all status buckets into a single sorted list of jobs with
// hour-float start/end already clamped to the visible window.
// Express service start/end as hour-floats relative to midnight of the
// viewed day in UTC. Anchoring to the day means multi-day services land
// at the workday open on continuation days and at the actual eta on the
// final day, instead of always painting the original start hour.
const buildWorkerJobs = (
  worker,
  includedStatuses,
  open,
  close,
  dayStartUtcSec
) => {
  const sources = [
    worker.acceptedServices,
    worker.inProgressServices,
    worker.pausedServices,
    worker.finishedServices,
    worker.canceledServices
  ];
  const dayEndUtcSec = dayStartUtcSec + 86400;
  const out = [];
  for (const list of sources) {
    if (!Array.isArray(list)) continue;
    for (const s of list) {
      if (!includedStatuses.includes(s.status)) continue;
      if (typeof s.timeRaw !== "number" || typeof s.etaRaw !== "number")
        continue;

      // Hours since midnight of viewed day. Negative means service began
      // on a previous day; > 24 means it ends on a later day.
      const startInDay = (s.timeRaw - dayStartUtcSec) / 3600;
      const endInDay = (s.etaRaw - dayStartUtcSec) / 3600;
      const window = clampToWindow(startInDay, endInDay, open, close);
      if (!window) continue;

      // The original day's start hour is useful for the "9 AM" label —
      // only show it when the service actually starts today; otherwise
      // the label is misleading for continuation rows.
      const startsToday = startInDay >= 0 && startInDay < 24;
      out.push({
        id: s.id,
        status: s.status,
        type: s.serviceTypeKey || "Service",
        truck: s.truck?.licencePlate || s.truck?.model || "",
        start: window.start,
        end: window.end,
        startRaw: startsToday ? startInDay : null,
        original: s
      });
    }
  }
  return out;
};

// Midnight UTC for the calendar date selected via the toolbar's date
// picker. The picker stores a Date in local time; we treat its
// year/month/day as the UTC calendar date so the FE matches the
// `date=YYYY-MM-DD` filter sent to the schedule endpoint.
const dayStartUtcSecFromDate = date => {
  if (!(date instanceof Date)) return null;
  return (
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) /
    1000
  );
};

// Position helpers
const xFromHour = (h, open) => (h - open) * HOUR_WIDTH;
const widthFromRange = (start, end) => Math.max(8, (end - start) * HOUR_WIDTH);

const TimelineBody = ({
  visibleWorkers,
  unassignedServices,
  showUnassigned,
  includedStatuses,
  workdayWindow,
  collapsedWorkers,
  onToggleWorkerCollapse,
  draggingId,
  draggingFromWorker,
  startDrag,
  endDrag,
  handleDrop,
  handleDropToUnassigned,
  nowH,
  isToday,
  dateQuery
}) => {
  const dispatch = useDispatch();
  const nameListRef = useRef(null);
  const timelineRef = useRef(null);

  const { open, close, totalH } = workdayWindow;
  const bodyWidth = totalH * HOUR_WIDTH;

  // Scroll-sync between left name column and right timeline body.
  const onNameScroll = e => {
    if (timelineRef.current) timelineRef.current.scrollTop = e.target.scrollTop;
  };
  const onTimelineScroll = e => {
    if (nameListRef.current) nameListRef.current.scrollTop = e.target.scrollTop;
  };

  const dayStartUtcSec = useMemo(
    () => dayStartUtcSecFromDate(dateQuery),
    [dateQuery]
  );

  // Pre-pack lanes for each worker.
  const perWorker = useMemo(() => {
    const out = {};
    for (const w of visibleWorkers) {
      const jobs = buildWorkerJobs(
        w,
        includedStatuses,
        open,
        close,
        dayStartUtcSec
      );
      out[w.id] = packLanes(jobs);
    }
    return out;
  }, [visibleWorkers, includedStatuses, open, close, dayStartUtcSec]);

  const rowHeight = workerId => {
    if (collapsedWorkers[workerId]) return 36;
    const lanes = perWorker[workerId]?.lanes?.length || 1;
    return Math.max(72, 14 + lanes * 52);
  };

  const totalJobsShown = useMemo(() => {
    let c = 0;
    for (const w of visibleWorkers) c += perWorker[w.id]?.lanes.flat().length || 0;
    return c;
  }, [perWorker, visibleWorkers]);

  // Pre-compute unassigned chip windows — clamp to workday so off-window
  // tickets still render visibly. Same day-relative math as
  // buildWorkerJobs so a chip and the eventual assigned bar render
  // identically (no surprises after drop).
  const unassignedChips = useMemo(() => {
    if (!showUnassigned || !Array.isArray(unassignedServices)) return [];
    return unassignedServices.map(s => {
      const startInDay =
        typeof s.time === "number" && dayStartUtcSec != null
          ? (s.time - dayStartUtcSec) / 3600
          : open;
      const durationH =
        typeof s.eta === "number" && typeof s.time === "number"
          ? Math.max(0, (s.eta - s.time) / 3600)
          : 1;
      const endInDay = startInDay + durationH;
      const window = clampToWindow(startInDay, endInDay, open, close) || {
        start: open,
        end: Math.min(open + 1, close)
      };
      return {
        id: s.id,
        original: s,
        type: s.serviceTypeKey || "Service",
        truck: s.truck?.licencePlate || s.truck?.model || "",
        startH: window.start,
        endH: window.end
      };
    });
  }, [unassignedServices, showUnassigned, open, close, dayStartUtcSec]);

  const openService = serviceId => {
    disableScroll();
    dispatch(openTicketDetails(serviceId));
  };

  // Empty state — pick a message that reflects the real reason the body
  // is empty (no selection vs. nothing scheduled vs. all unassigned only).
  if (visibleWorkers.length === 0 && unassignedChips.length === 0) {
    return (
      <TimelineBodyStyles>
        <div className="placeholder" style={{ width: "100%" }}>
          Nothing scheduled for this day
        </div>
      </TimelineBodyStyles>
    );
  }

  // Total counts for jobs+counts-on-name-row.
  const renderHourGrid = () =>
    Array.from({ length: Math.ceil(totalH) }).map((_, i) => (
      <div
        key={i}
        className="row-grid"
        style={{ left: i * HOUR_WIDTH, width: HOUR_WIDTH }}
      >
        <div className="half" style={{ left: HOUR_WIDTH / 2 }} />
      </div>
    ));

  return (
    <TimelineBodyStyles>
      {/* ----- Left name column ----- */}
      <div className="name-col">
        <div className="name-head">
          <span>Technician</span>
          <span>Jobs</span>
        </div>
        <div className="name-list" ref={nameListRef} onScroll={onNameScroll}>
          {showUnassigned && (
            <>
              <div
                className={`unassigned-head-name ${
                  draggingFromWorker ? "drop-active" : ""
                }`}
                onDragOver={e => {
                  if (draggingFromWorker) e.preventDefault();
                }}
                onDrop={() => {
                  if (draggingFromWorker) handleDropToUnassigned();
                }}
              >
                <span>Unassigned · {unassignedChips.length}</span>
                <span className="hint">
                  {draggingFromWorker ? "drop to unassign" : "drag to assign"}
                </span>
              </div>
              {unassignedChips.map(u => {
                const priority = u.original.priority || "normal";
                return (
                  <div
                    key={u.id}
                    className={`unassigned-row-name ${
                      draggingFromWorker ? "drop-active" : ""
                    }`}
                    style={{ height: UNASSIGNED_ROW_H }}
                    onDragOver={e => {
                      if (draggingFromWorker) e.preventDefault();
                    }}
                    onDrop={() => {
                      if (draggingFromWorker) handleDropToUnassigned();
                    }}
                  >
                    <span className={`priority-dot ${priority}`} />
                    <div className="un-meta">
                      <div className="type">{u.type}</div>
                      <div className="id-truck">
                        S-{u.id}
                        {u.truck ? ` · ${u.truck}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="section-spacer" />
            </>
          )}
          {visibleWorkers.map(w => {
            const collapsed = collapsedWorkers[w.id];
            const jobsCount = perWorker[w.id]?.lanes.flat().length || 0;
            return (
              <div
                key={w.id}
                className="worker-row-name"
                style={{ height: rowHeight(w.id) }}
                onClick={() => onToggleWorkerCollapse(w.id)}
              >
                <div className="chev">
                  <Chevron dir={collapsed ? "r" : "d"} size={11} />
                </div>
                <WorkerAvatar
                  firstName={w.firstName}
                  lastName={w.lastName}
                  profileImage={w.profileImage}
                  size={collapsed ? 22 : 28}
                />
                <div className="meta">
                  <div className="name">
                    {w.firstName} {w.lastName}
                  </div>
                  {!collapsed && w.role?.name && (
                    <div className="sub">{w.role.name}</div>
                  )}
                </div>
                <div className="count">{jobsCount}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ----- Right scrolling timeline ----- */}
      <div className="timeline" ref={timelineRef} onScroll={onTimelineScroll}>
        <div className="timeline-inner" style={{ width: bodyWidth }}>
          {/* Now-line — single element spanning all rows so toggling status
              filters doesn't reflow per-row line segments and visibly glitch
              the indicator. Only when viewing today and the time is in the
              workday window. */}
          {isToday && nowH >= open && nowH <= close && (
            <div className="now-line" style={{ left: xFromHour(nowH, open) }}>
              <div className="now-dot" />
            </div>
          )}

          <div className="hour-head">
            {Array.from({ length: Math.ceil(totalH) }).map((_, i) => (
              <div
                key={i}
                className="hour-cell"
                style={{ width: HOUR_WIDTH }}
              >
                {formatHourShort(open + i)}
              </div>
            ))}
          </div>

          {showUnassigned && (
            <>
              <div
                className={`unassigned-head ${
                  draggingFromWorker ? "drop-active" : ""
                }`}
                onDragOver={e => {
                  if (draggingFromWorker) e.preventDefault();
                }}
                onDrop={() => {
                  if (draggingFromWorker) handleDropToUnassigned();
                }}
              />
              {unassignedChips.map(u => {
                const left = xFromHour(u.startH, open) + 2;
                const width = widthFromRange(u.startH, u.endH) - 4;
                return (
                  <div
                    key={u.id}
                    className={`unassigned-row ${
                      draggingFromWorker ? "drop-active" : ""
                    }`}
                    style={{ height: UNASSIGNED_ROW_H }}
                    onDragOver={e => {
                      if (draggingFromWorker) e.preventDefault();
                    }}
                    onDrop={() => {
                      if (draggingFromWorker) handleDropToUnassigned();
                    }}
                  >
                    {renderHourGrid()}
                    <div
                      className="un-chip"
                      draggable
                      onDragStart={() => startDrag(u.id)}
                      onDragEnd={endDrag}
                      onClick={e => {
                        // Suppress click that fires after a drop on the
                        // same element. draggingId is reset to null by
                        // the time a real (no-drag) click reaches here.
                        if (draggingId != null) return;
                        e.stopPropagation();
                        openService(u.id);
                      }}
                      style={{
                        left,
                        top: 6,
                        width,
                        height: UNASSIGNED_ROW_H - 12
                      }}
                    >
                      <div className="un-chip-meta">
                        <span
                          className={`priority-dot ${
                            u.original.priority || "normal"
                          }`}
                        />
                        <div className="stack">
                          <div className="type">{u.type}</div>
                          {u.truck && <div className="truck">{u.truck}</div>}
                        </div>
                      </div>
                      <div className="un-chip-time">
                        {formatHour(u.startH)}–{formatHour(u.endH)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="section-spacer" />
            </>
          )}

          {visibleWorkers.map(w => {
            const { lanes, conflictIds } = perWorker[w.id] || {
              lanes: [],
              conflictIds: new Set()
            };
            const collapsed = collapsedWorkers[w.id];
            const h = rowHeight(w.id);
            const flatJobs = lanes.flat();
            return (
              <div
                key={w.id}
                className="worker-row"
                style={{ height: h }}
                onDragOver={e => {
                  if (draggingId != null) e.preventDefault();
                }}
                onDrop={() => handleDrop(w.id)}
              >
                {renderHourGrid()}

                {/* Lunch zone: 12:00 - 12:30 */}
                {12 >= open && 12 < close && (
                  <div
                    className="lunch"
                    style={{
                      left: xFromHour(12, open),
                      width: HOUR_WIDTH / 2
                    }}
                  />
                )}

                {draggingId != null && !collapsed && (
                  <div className="drop-target" />
                )}

                {collapsed
                  ? flatJobs.map(j => {
                      const palette = themeForErp(j.status);
                      return (
                        <div
                          key={j.id}
                          className="compact-bar"
                          style={{
                            left: xFromHour(j.start, open) + 2,
                            width: widthFromRange(j.start, j.end) - 4,
                            ["--bar-color"]: palette.chip
                          }}
                        />
                      );
                    })
                  : lanes.map((lane, li) =>
                      lane.map(j => {
                        const palette = themeForErp(j.status);
                        const isConflict = conflictIds.has(j.id);
                        const left = xFromHour(j.start, open) + 2;
                        const width = widthFromRange(j.start, j.end) - 4;
                        const short = width < 90;
                        const isDraggingThis =
                          draggingId != null && draggingId === j.id;
                        return (
                          <div
                            key={j.id}
                            className={`job ${isConflict ? "conflict" : ""} ${
                              isDraggingThis ? "dragging" : ""
                            }`}
                            style={{
                              left,
                              top: 7 + li * 52,
                              width,
                              ["--job-bg"]: palette.bg,
                              ["--job-fg"]: palette.fg,
                              ["--job-accent"]: palette.chip
                            }}
                            draggable
                            onDragStart={e => {
                              e.stopPropagation();
                              startDrag(j.id, w.id);
                            }}
                            onDragEnd={endDrag}
                            onClick={() => {
                              // Suppress click that fires on a drop ending
                              // on the same element. dragging flag is set
                              // synchronously by startDrag so a real click
                              // (no drag) reaches this branch.
                              if (draggingId != null) return;
                              openService(j.id);
                            }}
                          >
                            <div className="row1">
                              <div className="type">{j.type}</div>
                              {isConflict && (
                                <span className="warn">
                                  <WarnIcon size={11} />
                                </span>
                              )}
                            </div>
                            {!short && (
                              <div className="row2">
                                <span className="truck">{j.truck}</span>
                                <span className="start">
                                  {j.startRaw != null
                                    ? formatHour(j.startRaw).replace(/:00 /, " ")
                                    : "ongoing"}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
              </div>
            );
          })}
        </div>
      </div>
    </TimelineBodyStyles>
  );
};

export const computeTotalJobs = (visibleWorkers, includedStatuses) => {
  let c = 0;
  for (const w of visibleWorkers) {
    const lists = [
      w.acceptedServices,
      w.inProgressServices,
      w.pausedServices,
      w.finishedServices,
      w.canceledServices
    ];
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const s of list) if (includedStatuses.includes(s.status)) c++;
    }
  }
  return c;
};

export default TimelineBody;
